// simple-polygon-map-entity.ts
import { Color, Cartesian3, JulianDate, TimeInterval, TimeIntervalCollection, ColorMaterialProperty } from 'cesium';
import { ShapeMapEntity } from './shape-map-entity.service';
import { CameraService } from './camera.service';
import { CoreService } from './core.service';
import * as Cesium from 'cesium';
import { WindowService } from './window.service';
import { DataContextService } from './data-context.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TimelineService } from './timeline.service';
export class SimplePolygonMapEntity extends ShapeMapEntity {
  private locations: [number, number][] = [];
  private extrudedHeight?: number;
  private height?: number;
  private material: Color;
  private outlineColor?: Color;
  private showOutline = false;
  private outlineWidth = 1.0;
  private centerLatitude?: number;
  private centerLongitude?: number;

  constructor(
    mapEntityName: string,
    mapEntityLayerPath: string,
    cameraService: CameraService,
    coreService: CoreService,
    windowService: WindowService,
    dataContext: DataContextService,
    modalService: NgbModal,
    timelineService: TimelineService
  ) {
    super(mapEntityName, mapEntityLayerPath, cameraService, coreService, windowService, dataContext, modalService, timelineService);
    this.material = new Color(1.0, 0.0, 0.0, 0.2);
  }

  override init(mapEntityName: string, mapEntityLayerPath: string, locations?: [number, number][], altitude?: number, height?: number, material?: Color) {
    super.init(mapEntityName, mapEntityLayerPath);
    if (locations) {
      this.locations = locations;
      this.extrudedHeight = altitude;
      this.height = height;
      if (material) this.material = material;

      const centerLocations = this.center(locations);
      if (centerLocations?.length === 2) {
        this.setCenterLatitude(centerLocations[0]);
        this.setCenterLongitude(centerLocations[1]);
      }
    }
  }

  private center(locations: [number, number][]): [number, number] {
    const latSum = locations.reduce((sum, loc) => sum + loc[0], 0);
    const lonSum = locations.reduce((sum, loc) => sum + loc[1], 0);
    return [latSum / locations.length, lonSum / locations.length];
  }

  override setCenterLatitude(latitude: number) {
    this.centerLatitude = latitude;
  }

  override setCenterLongitude(longitude: number) {
    this.centerLongitude = longitude;
  }

  setMaterial(material: any): void {
    this.material = material;
    if (this.cesiumEntity?.polygon) {
      this.cesiumEntity.polygon.material = this.material;
    }
  }


  private updatePolygonMaterial(): void {
    if (!this.cesiumEntity) {
      this.buildEntity();
    }
    if (this.cesiumEntity?.polygon) {
      this.cesiumEntity.polygon.material = this.material;
    }
  }

  getMaterial(): Color {
    return this.material;
  }

  override setOutline(outlineColor: Color) {
    this.outlineColor = outlineColor;
    if (this.cesiumEntity?.polygon) {
      this.cesiumEntity.polygon.outlineColor = outlineColor;
    }
    if (this.cesiumEntity?.polyline) {
      this.cesiumEntity.polyline.material = new Cesium.ColorMaterialProperty(outlineColor);
    }
  }

  override setOutlineWidth(outlineWidth: number) {
    this.outlineWidth = outlineWidth;
    if (this.cesiumEntity?.polygon) {
      this.cesiumEntity.polygon.outlineWidth = outlineWidth;
    }
    if (this.cesiumEntity?.polyline) {
      this.cesiumEntity.polyline.width = outlineWidth;
    }
  }

  override setShowOutline(showOutline: boolean) {
    this.showOutline = showOutline;
    if (this.cesiumEntity?.polygon) {
      this.cesiumEntity.polygon.outline = showOutline;
    }
    if (this.cesiumEntity?.polyline) {
      this.cesiumEntity.polyline.show = showOutline;
    }
  }

  setCoordinates(locations: [number, number][]) {
    this.locations = locations;
    const centerLocations = this.center(locations);
    if (centerLocations?.length === 2) {
      this.setCenterLatitude(centerLocations[0]);
      this.setCenterLongitude(centerLocations[1]);
    }

    if (this.cesiumEntity) {
      this.setHierarchy();
    }
  }

  setHeight(height: number) {
    this.height = height;
    if (this.cesiumEntity) {
      this.setHierarchy();
    }
  }

  override getHeight(): number {
    return this.height ?? 0;
  }

  // Add this method to get floor-specific height
  getFloorSpecificHeight(): number {
    // Return the actual floor height, not extruded height from ground
    return this.height ?? 1.0; // Default floor thickness
  }

  // Update the setAltitude method to handle floor-specific rendering
  // Update the setAltitude method to use thinner floors
  setAltitude(altitude: number) {
    this.extrudedHeight = altitude;
    if (this.cesiumEntity?.polygon) {
      this.cesiumEntity.polygon.perPositionHeight = undefined;
      // Set floor-specific height instead of extruding from ground
      this.cesiumEntity.polygon.height = altitude;
      // FIX: Use much thinner floor thickness (10cm instead of 1m)
      this.cesiumEntity.polygon.extrudedHeight = altitude + 0.5; // Only 10cm thick
    }
  }


  getAltitude(): number | undefined {
    return this.extrudedHeight;
  }

  getCoordinates(): [number, number][] {
    return this.locations;
  }

  public arrayOfLocationsToArrayOfPoints(locations: [number, number][]): number[] {
    const arrayOfPoints: number[] = [];
    for (const [lat, lon] of locations) {
      arrayOfPoints.push(lon, lat);
      if (this.height !== undefined && this.height !== 0) {
        arrayOfPoints.push(this.height);
      }
    }
    return arrayOfPoints;
  }

  private getClosedPolylinePoints(): number[] {
    const rawPoints = this.arrayOfLocationsToArrayOfPoints(this.locations);
    const polylinePoints = [...rawPoints];
    if (this.locations.length > 0) {
      const first = this.locations[0];
      const last = this.locations[this.locations.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        polylinePoints.push(first[1], first[0]);
        if (this.height !== undefined && this.height !== 0) {
          polylinePoints.push(this.height);
        }
      }
    }
    return polylinePoints;
  }

  private setHierarchy() {
    if (!this.cesiumEntity) return;

    const isGround = (!this.extrudedHeight || this.extrudedHeight === 0) && (!this.height || this.height === 0);
    const points = this.arrayOfLocationsToArrayOfPoints(this.locations);
    const closedPoints = this.getClosedPolylinePoints();

    if (this.cesiumEntity.polygon) {
      this.cesiumEntity.polygon.hierarchy = (!isGround && this.height)
        ? Cartesian3.fromDegreesArrayHeights(points)
        : Cartesian3.fromDegreesArray(points);
      this.cesiumEntity.polygon.material = new ColorMaterialProperty(this.material);
    }

    if (this.cesiumEntity.polyline) {
      this.cesiumEntity.polyline.positions = (!isGround && this.height)
        ? Cartesian3.fromDegreesArrayHeights(closedPoints)
        : Cartesian3.fromDegreesArray(closedPoints);
      this.cesiumEntity.polyline.material = new ColorMaterialProperty(this.outlineColor || this.material);
    }
  }

  override buildEntity() {
    super.buildEntity();

    const isGround = (!this.extrudedHeight || this.extrudedHeight === 0) && (!this.height || this.height === 0);
    const rawPoints = this.arrayOfLocationsToArrayOfPoints(this.locations);
    const closedPoints = this.getClosedPolylinePoints();

    this.cesiumEntity = new Cesium.Entity({
      id: this.id,
      name: this.name,
      polygon: {
        hierarchy: (!isGround && this.height)
          ? Cesium.Cartesian3.fromDegreesArrayHeights(rawPoints)
          : Cesium.Cartesian3.fromDegreesArray(rawPoints),
        material: new Cesium.ColorMaterialProperty(this.material),
        heightReference: isGround ? Cesium.HeightReference.CLAMP_TO_GROUND : undefined,
        classificationType: isGround ? Cesium.ClassificationType.TERRAIN : undefined,
        height: isGround ? 0 : (this.extrudedHeight ?? 0),
        extrudedHeight: isGround ? undefined : ((this.extrudedHeight ?? 0) + 0.3),
        outline: false,
        outlineColor: this.outlineColor,
        outlineWidth: this.outlineWidth,
        perPositionHeight: undefined,
        show: true
      },
      polyline: {
        positions: (!isGround && this.height)
          ? Cesium.Cartesian3.fromDegreesArrayHeights(closedPoints)
          : Cesium.Cartesian3.fromDegreesArray(closedPoints),
        width: isGround ? (this.outlineWidth || 3.0) : this.outlineWidth,
        material: new Cesium.ColorMaterialProperty(this.outlineColor || this.material),
        clampToGround: isGround,
        show: this.showOutline
      },
      availability: this.timestamp
        ? new Cesium.TimeIntervalCollection([
          new Cesium.TimeInterval({
            start: Cesium.JulianDate.fromDate(this.timestamp),
            stop: new Cesium.JulianDate(3448257, 0)
          })
        ])
        : undefined
    });
    this.cesiumEntity.arcLiveMapEntity = this;
  }

  override getCesiumEntity(): any {
    if (!this.cesiumEntity) {
      this.buildEntity();
    }
    return this.cesiumEntity;
  }

  override setAddedToMap(status: boolean): void {
    this.addedToMap = status;
  }
}
